/**
 * Servicio para gestión de tesorería
 * Maneja todas las llamadas a la API de tesorería
 */
import { apiClient } from './api';

const TESORERIA_BASE = '/tesoreria';

export const tesoreriaService = {
  // ==========================================
  // MOVIMIENTOS
  // ==========================================

  /**
   * Listar movimientos con filtros opcionales
   */
  cargarMovimientos: (rutEmpresa, anio, mes, estado = null) => {
    let endpoint = `${TESORERIA_BASE}/movimientos?rut_empresa=${rutEmpresa}&anio=${anio}&mes=${mes}`;
    if (estado) {
      endpoint += `&estado=${estado}`;
    }
    return apiClient.get(endpoint);
  },

  /**
   * Obtener un movimiento específico
   */
  obtenerMovimiento: (movimientoId) => {
    return apiClient.get(`${TESORERIA_BASE}/movimientos/${movimientoId}`);
  },

  /**
   * Actualizar datos contables de un movimiento
   */
  actualizarMovimiento: (movimientoId, datos) => {
    return apiClient.put(`${TESORERIA_BASE}/movimientos/${movimientoId}`, datos);
  },

  // ==========================================
  // PENDIENTES (BADGE)
  // ==========================================

  /**
   * Obtener información sobre pendientes de completar/exportar
   * Retorna: { total_movimientos, pendientes_completar, completados, exportados, porcentaje_completado }
   */
  obtenerPendientes: (rutEmpresa, anio, mes) => {
    const endpoint = `${TESORERIA_BASE}/pendientes-exportar?rut_empresa=${rutEmpresa}&anio=${anio}&mes=${mes}`;
    return apiClient.get(endpoint);
  },

  // ==========================================
  // IMPORTACIÓN
  // ==========================================

  /**
   * Importar archivo Excel con movimientos
   * @param {File} archivo - Archivo Excel a importar
   * @param {string} rutEmpresa - RUT de la empresa
   * @param {string} dvEmpresa - DV de la empresa
   * @param {string} periodo - Período opcional (YYYYMM)
   * @param {string} modoDuplicados - IGNORAR | ACTUALIZAR | RECHAZAR
   * @returns {Promise} Resultado de la importación
   */
  importarArchivo: (archivo, rutEmpresa, dvEmpresa, periodo = null, modoDuplicados = 'IGNORAR') => {
    const formData = new FormData();
    formData.append('archivo', archivo);

    let endpoint = `${TESORERIA_BASE}/importar?rut_empresa=${rutEmpresa}&dv_empresa=${dvEmpresa}&modo_duplicados=${modoDuplicados}`;
    if (periodo) {
      endpoint += `&periodo=${periodo}`;
    }

    return apiClient.uploadFile(endpoint, formData);
  },

  // ==========================================
  // EXPORTACIONES
  // ==========================================

  /**
   * Listar exportaciones realizadas
   */
  listarExportaciones: (rutEmpresa, anio = null, mes = null) => {
    let endpoint = `${TESORERIA_BASE}/exportaciones?rut_empresa=${rutEmpresa}`;
    if (anio) endpoint += `&anio=${anio}`;
    if (mes) endpoint += `&mes=${mes}`;
    return apiClient.get(endpoint);
  },

  /**
   * Obtener detalles de una exportación
   */
  obtenerExportacion: (exportacionId) => {
    return apiClient.get(`${TESORERIA_BASE}/exportaciones/${exportacionId}`);
  },

  /**
   * Obtener el folio actual para la próxima exportación
   */
  obtenerFolioActual: (rutEmpresa) => {
    return apiClient.get(`${TESORERIA_BASE}/folio-actual?rut_empresa=${rutEmpresa}`);
  },

  /**
   * Exportar movimientos completados a CSV
   */
  exportarMovimientos: (rutEmpresa, anio, mes, folioInicial = null, soloPendientes = false, forzar = false) => {
    let endpoint = `${TESORERIA_BASE}/exportar?rut_empresa=${rutEmpresa}&anio=${anio}&mes=${mes}&solo_pendientes=${soloPendientes}&forzar=${forzar}`;
    if (folioInicial) {
      endpoint += `&folio_inicial=${folioInicial}`;
    }
    return apiClient.post(endpoint, null);
  },

  /**
   * Descargar archivo CSV de una exportación
   * Retorna un Blob que puede ser descargado
   */
  descargarExportacion: (exportacionId) => {
    return apiClient.get(
      `${TESORERIA_BASE}/exportaciones/${exportacionId}/descargar`,
      {
        responseType: 'blob'
      }
    );
  },

  // ==========================================
  // IMPORTACIONES
  // ==========================================

  /**
   * Listar importaciones de tesorería
   */
  listarImportaciones: (rutEmpresa, anio = null, mes = null) => {
    let endpoint = `${TESORERIA_BASE}/importaciones?rut_empresa=${rutEmpresa}`;
    if (anio && mes) {
      endpoint += `&anio=${anio}&mes=${mes}`;
    }
    return apiClient.get(endpoint);
  },

  /**
   * Eliminar una importación y todos sus movimientos asociados
   */
  eliminarImportacion: (importacionId) => {
    return apiClient.delete(`${TESORERIA_BASE}/importaciones/${importacionId}`);
  }
};

export default tesoreriaService;
